import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TablePagination,
  TableHead, TableRow, Paper, IconButton, Button, Box, CircularProgress, TextField,
  Chip, Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AchievementsService from '../../services/AchievementsService';
import { permissionOptions } from '../../utils/Enums';
import { getIcon } from '../../utils/IconHandler';
import AddEditAchievementModal from './AddEditAchievementModal';
import DeleteAchievementModal from './DeleteAchievementModal';
import AssignAchievementModal from './AssignAchievementModal';
import SnackbarUtils from '../../utils/SnackbarUtils';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';

const CATEGORY_OPTIONS = [
  { value: 'sales', label: 'Ventas' },
  { value: 'tickets', label: 'Tickets' },
  { value: 'clients', label: 'Clientes' },
  { value: 'growth', label: 'Crecimiento' }
];

const ICON_OPTIONS = [
  { value: 1, label: '💰' },
  { value: 2, label: '⭐' },
  { value: 3, label: '🏆' },
  { value: 4, label: '👑' },
  { value: 5, label: '🎫' },
  { value: 6, label: '🎯' },
  { value: 7, label: '🚀' },
  { value: 8, label: '💫' },
  { value: 9, label: '📈' },
  { value: 10, label: '📊' },
  { value: 11, label: '💥' },
  { value: 12, label: '💎' },
  { value: 13, label: '💠' },
  { value: 14, label: '✨' },
  { value: 15, label: '🔥' },
  { value: 16, label: '⚡' },
  { value: 17, label: '🌟' },
  { value: 18, label: '🏅' }
];

const AchievementsTable = () => {
  const { t } = useTranslation();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [assignments, setAssignments] = useState({});

  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const hasPermission = (permissionId) => userPermissions.includes(permissionId);
  const canManage = hasPermission(permissionOptions.find(p => p.label === 'MANAGE_OBJECTIVES')?.id);

  useEffect(() => {
    loadAchievements();
  }, []);

  // Cargar asignaciones para todos los logros al cargar la página
  useEffect(() => {
    const loadAllAssignments = async () => {
      if (achievements.length > 0) {
        const assignmentPromises = achievements.map(async (achievement) => {
          try {
            const assignmentData = await AchievementsService.getAchievementAssignments(achievement.id);
            return { achievementId: achievement.id, assignments: assignmentData };
          } catch (error) {
            console.error(`Error loading assignments for achievement ${achievement.id}:`, error);
            return { achievementId: achievement.id, assignments: [] };
          }
        });

        const results = await Promise.all(assignmentPromises);
        const assignmentsMap = {};
        results.forEach(({ achievementId, assignments }) => {
          assignmentsMap[achievementId] = assignments;
        });
        setAssignments(assignmentsMap);
      }
    };

    loadAllAssignments();
  }, [achievements]);

  const loadAchievements = async () => {
    try {
      setLoading(true);
      const data = await AchievementsService.getAchievements();
      setAchievements(data || []);
    } catch (error) {
      console.error('Error loading achievements:', error);
      SnackbarUtils.error(t('error_loading_achievements') || 'Error al cargar logros');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleAdd = () => {
    setSelectedAchievement(null);
    setAddModalOpen(true);
  };

  const handleEdit = (achievement) => {
    setSelectedAchievement(achievement);
    setEditModalOpen(true);
  };

  const handleDelete = (achievement) => {
    setSelectedAchievement(achievement);
    setDeleteModalOpen(true);
  };

  const handleAssign = (achievement) => {
    setSelectedAchievement(achievement);
    setAssignModalOpen(true);
  };

  const handleToggleExpand = async (achievementId) => {
    const isExpanded = expandedRows[achievementId];
    setExpandedRows({
      ...expandedRows,
      [achievementId]: !isExpanded
    });

    // Cargar asignaciones si no están cargadas
    if (!isExpanded && !assignments[achievementId]) {
      try {
        const assignmentData = await AchievementsService.getAchievementAssignments(achievementId);
        setAssignments({
          ...assignments,
          [achievementId]: assignmentData
        });
      } catch (error) {
        console.error('Error loading assignments:', error);
        // Si el endpoint no existe aún, usar array vacío
        setAssignments({
          ...assignments,
          [achievementId]: []
        });
      }
    }
  };

  const handleSave = async () => {
    await loadAchievements();
    setAddModalOpen(false);
    setEditModalOpen(false);
    setSelectedAchievement(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      await AchievementsService.deleteAchievement(selectedAchievement.id);
      SnackbarUtils.success(t('achievement_deleted') || 'Logro eliminado exitosamente');
      await loadAchievements();
      setDeleteModalOpen(false);
      setSelectedAchievement(null);
    } catch (error) {
      console.error('Error deleting achievement:', error);
      SnackbarUtils.error(t('error_deleting_achievement') || 'Error al eliminar logro');
    }
  };

  const filteredAchievements = achievements.filter(achievement =>
    achievement.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    achievement.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    achievement.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedAchievements = filteredAchievements.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getCategoryLabel = (category) => {
    return CATEGORY_OPTIONS.find(opt => opt.value === category)?.label || category;
  };

  const getIconLabel = (iconType) => {
    return ICON_OPTIONS.find(opt => opt.value === iconType)?.label || '🏅';
  };

  return (
    <>
      <Paper sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <TextField
            label={t('search') || 'Buscar'}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: '300px' }}
          />
          {canManage && (
            <Button
              variant="contained"
              startIcon={getIcon('add')}
              onClick={handleAdd}
            >
              {t('add_achievement') || 'Agregar Logro'}
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="left">{t('icon') || 'Icono'}</TableCell>
                    <TableCell>{t('name') || 'Nombre'}</TableCell>
                    <TableCell>{t('description') || 'Descripción'}</TableCell>
                    <TableCell>{t('category') || 'Categoría'}</TableCell>
                    <TableCell>{t('suggested_value') || 'Valor Sugerido'}</TableCell>
                    <TableCell>{t('status') || 'Estado'}</TableCell>
                    {canManage && <TableCell align="right">{t('actions') || 'Acciones'}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedAchievements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canManage ? 7 : 6} align="center">
                        {t('no_achievements') || 'No hay logros'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedAchievements.map((achievement) => {
                      // Normalizar isActive (camelCase del backend) a is_active (snake_case usado en frontend)
                      const isActive = achievement.isActive ?? achievement.is_active ?? false;
                      // Normalizar iconType (camelCase del backend) a icon_type (snake_case usado en frontend)
                      const iconType = achievement.iconType ?? achievement.icon_type ?? 1;
                      // Normalizar defaultRequirementValue
                      const defaultRequirementValue = achievement.defaultRequirementValue ?? achievement.default_requirement_value ?? achievement.requirementValue ?? achievement.requirement_value ?? achievement.targetValue ?? achievement.target_value ?? 0;
                      const isExpanded = expandedRows[achievement.id];
                      const achievementAssignments = assignments[achievement.id] || [];
                      
                      return (
                        <React.Fragment key={achievement.id}>
                          <TableRow>
                            <TableCell align="left">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {achievementAssignments.length > 0 && (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleToggleExpand(achievement.id)}
                                    >
                                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                    <Chip
                                      label={`${achievementAssignments.length} ${achievementAssignments.length === 1 ? t('assignment') || 'asignación' : t('assignments') || 'asignaciones'}`}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      onClick={() => handleToggleExpand(achievement.id)}
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  </>
                                )}
                                <span style={{ fontSize: '24px' }}>{getIconLabel(iconType)}</span>
                              </Box>
                            </TableCell>
                            <TableCell>{achievement.name}</TableCell>
                            <TableCell>{achievement.description || '-'}</TableCell>
                            <TableCell>{getCategoryLabel(achievement.category)}</TableCell>
                            <TableCell>
                              {defaultRequirementValue > 0 ? defaultRequirementValue.toLocaleString() : '-'}
                            </TableCell>
                            <TableCell>
                              {isActive ? (
                                <span style={{ color: 'green' }}>{t('active') || 'Activo'}</span>
                              ) : (
                                <span style={{ color: 'gray' }}>{t('inactive') || 'Inactivo'}</span>
                              )}
                            </TableCell>
                            {canManage && (
                              <TableCell align="right">
                                <IconButton onClick={() => handleAssign(achievement)} title={t('assign') || 'Asignar'}>
                                  {getIcon('add')}
                                </IconButton>
                                <IconButton onClick={() => handleEdit(achievement)}>
                                  {getIcon('settings')}
                                </IconButton>
                                <IconButton onClick={() => handleDelete(achievement)}>
                                  {getIcon('trash')}
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                          {isExpanded && achievementAssignments.length > 0 && (
                            <TableRow>
                              <TableCell colSpan={canManage ? 7 : 6} sx={{ py: 2, bgcolor: 'background.default' }}>
                                <Box sx={{ pl: 4 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    {t('assignments') || 'Asignaciones'}:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {(() => {
                                      // Agrupar asignaciones por mes/año
                                      const monthNames = [
                                        t('january') || 'Enero', t('february') || 'Febrero', t('march') || 'Marzo',
                                        t('april') || 'Abril', t('may') || 'Mayo', t('june') || 'Junio',
                                        t('july') || 'Julio', t('august') || 'Agosto', t('september') || 'Septiembre',
                                        t('october') || 'Octubre', t('november') || 'Noviembre', t('december') || 'Diciembre'
                                      ];
                                      
                                      const grouped = achievementAssignments.reduce((acc, assignment) => {
                                        const monthYear = `${assignment.month || assignment.month_number || ''}-${assignment.year || ''}`;
                                        if (!acc[monthYear]) {
                                          acc[monthYear] = [];
                                        }
                                        acc[monthYear].push(assignment);
                                        return acc;
                                      }, {});
                                      
                                      return Object.entries(grouped).map(([monthYear, assignments]) => {
                                        const [month, year] = monthYear.split('-');
                                        const monthName = monthNames[parseInt(month, 10) - 1] || month;
                                        return (
                                          <Box key={monthYear} sx={{ mb: 1 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                              {monthName} {year}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 2 }}>
                                              {assignments.map((assignment) => (
                                                <Chip
                                                  key={assignment.id}
                                                  label={
                                                    assignment.scope === 'GLOBAL'
                                                      ? `🌍 ${t('global') || 'Global'}: ${assignment.requirement_value?.toLocaleString() || ''}`
                                                      : assignment.scope === 'BRANCH'
                                                      ? `🏢 ${assignment.branch_name || t('branch') || 'Sucursal'}: ${assignment.requirement_value?.toLocaleString() || ''}`
                                                      : `👤 ${assignment.user_name || t('user') || 'Usuario'}: ${assignment.requirement_value?.toLocaleString() || ''}`
                                                  }
                                                  color="primary"
                                                  variant="outlined"
                                                  size="small"
                                                />
                                              ))}
                                            </Box>
                                          </Box>
                                        );
                                      });
                                    })()}
                                  </Box>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredAchievements.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {addModalOpen && (
        <AddEditAchievementModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSave={handleSave}
        />
      )}

      {editModalOpen && selectedAchievement && (
        <AddEditAchievementModal
          open={editModalOpen}
          achievement={selectedAchievement}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedAchievement(null);
          }}
          onSave={handleSave}
        />
      )}

      {deleteModalOpen && selectedAchievement && (
        <DeleteAchievementModal
          open={deleteModalOpen}
          achievement={selectedAchievement}
          onClose={() => {
            setDeleteModalOpen(false);
            setSelectedAchievement(null);
          }}
          onDelete={handleDeleteConfirm}
        />
      )}

      {assignModalOpen && selectedAchievement && (
        <AssignAchievementModal
          open={assignModalOpen}
          achievement={selectedAchievement}
          onClose={() => {
            setAssignModalOpen(false);
            setSelectedAchievement(null);
          }}
          onSave={async (response) => {
            // Si la respuesta contiene las asignaciones, usarlas directamente
            // Si no, recargar las asignaciones del logro específico
            try {
              let assignmentData = null;
              
              // Verificar si la respuesta contiene las asignaciones creadas
              if (response && Array.isArray(response)) {
                // Si es un array, son las asignaciones creadas
                assignmentData = response;
              } else if (response && response.data && Array.isArray(response.data)) {
                // Si está dentro de response.data
                assignmentData = response.data;
              } else if (response && response.assignments && Array.isArray(response.assignments)) {
                // Si está en response.assignments
                assignmentData = response.assignments;
              } else {
                // Si no está en la respuesta, recargar desde el backend
                assignmentData = await AchievementsService.getAchievementAssignments(selectedAchievement.id);
              }
              
              // Actualizar las asignaciones en el estado
              setAssignments({
                ...assignments,
                [selectedAchievement.id]: assignmentData
              });
              
              // Si estaba expandido, mantenerlo expandido
              if (!expandedRows[selectedAchievement.id] && assignmentData.length > 0) {
                setExpandedRows({
                  ...expandedRows,
                  [selectedAchievement.id]: true
                });
              }
            } catch (error) {
              console.error('Error updating assignments:', error);
              // Si falla, intentar recargar desde el backend
              try {
                const assignmentData = await AchievementsService.getAchievementAssignments(selectedAchievement.id);
                setAssignments({
                  ...assignments,
                  [selectedAchievement.id]: assignmentData
                });
              } catch (reloadError) {
                console.error('Error reloading assignments:', reloadError);
              }
            }
            setAssignModalOpen(false);
            setSelectedAchievement(null);
          }}
        />
      )}
    </>
  );
};

export default AchievementsTable;

